import { track } from '@vercel/analytics'
import { ANIMATION_TIMINGS, getDefaultCameraPosition } from './camera/CameraConfig.js'
import {
  getSaxavordCameraPosition,
  getSceneHotspots
} from './camera/CameraService.js'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import { getContentForPOI } from './content/TextContent.jsx'

// Helper function to get label text for POI names
function getPOILabel(poiName, isLandscape = true) {
  const landscapeLabels = {
    'POI_HA1': 'Hangars',
    'POI_LP1': 'Launch Pads',
    'POI_SAT': 'Antenna Pads',
    'POI_MET': 'Met Station',
    'POI_RAF': 'RAF Heritage',
    'POI_HIS': 'Bronze Age Burial Sites'
  }
  
  const portraitLabels = {
    'POI_HA1': 'Hangars',
    'POI_LP1': 'Launch Pads',
    'POI_SAT': 'Antenna Pads',
    'POI_MET': 'Met Station',
    'POI_RAF': 'RAF Heritage',
    'POI_HIS': 'Bronze Age Burial Sites'
  }
  
  const labelMap = isLandscape ? landscapeLabels : portraitLabels
  return labelMap[poiName] || poiName
}

// Lookup map: POI name → unique GTM event name


// Hotspots component - projects POI world positions to screen coords every frame.
// Runs at useFrame priority 2 — AFTER SceneManager's render (priority 1) — so
// camera1Ref matrices are already finalised for this frame before we project.
// Writes directly to DOM elements via hotspotsBridgeRef.dotRefsMap, bypassing
// React state and eliminating the stale-frame lag that came from the old two-loop
// architecture (useFrame → window.__hotspotsFrame → separate rAF in HotspotsOverlay).
export function Hotspots({ scenesRef, cameraRef, hotspotsBridgeRef }) {
  const { size } = useThree()

  // Cache device flags once — they don't change mid-session
  const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0)
  const isTabletDevice = isTouchDevice && window.innerWidth > 767 && window.innerWidth <= 1366
  const isDotMode = window.innerWidth <= 768 || isTabletDevice
  const DOT_OFFSET_X = isDotMode ? 50 : 0
  const DOT_OFFSET_Y = 0

  // Track last-seen hotspot name string to detect scene load/unload
  const lastNamesRef = useRef('')

  useFrame(() => {
    if (!scenesRef || !cameraRef?.current || !hotspotsBridgeRef?.current) return

    const hotspots = getSceneHotspots(scenesRef, 0, cameraRef.current, size)

    // ── Seed React list when scene loads / hotspot set changes ────────────────
    // seedHotspots triggers a single React state update that mounts the DOM nodes.
    // Position writes below run every frame but only touch already-mounted nodes.
    const names = hotspots.map(h => h.name).join(',')
    if (names !== lastNamesRef.current) {
      lastNamesRef.current = names
      hotspotsBridgeRef.current.seedHotspots?.(hotspots)
      // DOM nodes don't exist yet this frame — positions will be written next frame
      return
    }

    // ── Write positions directly to DOM elements ───────────────────────────────
    // No React setState, no re-render, no second rAF loop.
    const { dotRefsMap } = hotspotsBridgeRef.current
    if (!dotRefsMap) return

    for (let i = 0; i < hotspots.length; i++) {
      const h = hotspots[i]
      const el = dotRefsMap.get(h.name)
      if (!el) continue

      el.style.left = (h.x + DOT_OFFSET_X) + 'px'
      el.style.top  = (h.y + DOT_OFFSET_Y) + 'px'
      el.style.visibility = h.isVisible ? '' : 'hidden'
    }
  }, 2) // priority 2 → runs after SceneManager render (priority 1)

  return null
}

// NT Icon component for heritage sites
function NTIcon({ hotspot, leftPosition, topPosition, scene1LabelOpacity, isMobile, handleHotspotClick }) {
  const [isHovered, setIsHovered] = useState(false)
  
  return (
    <div
      className="nt-icon-container"
      style={{
        position: 'absolute',
        left: leftPosition,
        top: topPosition,
        opacity: scene1LabelOpacity,
        transition: isMobile ? 'none' : 'opacity 0.05s linear',
        transform: 'translate(-50%, -50%)',
        pointerEvents: scene1LabelOpacity < 0.95 ? 'none' : 'auto',
        width: '46px',
        height: '46px',
        borderRadius: '50%',
        backgroundColor: '#dbdbdb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => handleHotspotClick(hotspot)}
    >
      <img
        src="/textures/icons/scotnattrust.svg"
        alt="Scottish National Trust"
        className="nt-icon"
        style={{
          width: '26px',
          height: '26px',
          filter: isHovered 
            ? 'brightness(0) saturate(100%) invert(46%) sepia(94%) saturate(2572%) hue-rotate(1deg) brightness(103%) contrast(103%)'
            : 'brightness(0) saturate(100%) invert(29%) sepia(97%) saturate(1449%) hue-rotate(188deg) brightness(94%) contrast(101%)',
          pointerEvents: 'none',
          transition: 'none'
        }}
      />
    </div>
  )
}

// HotspotsOverlay - DOM overlay component to be rendered outside Canvas
export function HotspotsOverlay({ isAtPOI, onPOIStateChange, scenesRef, onBackToDefaultReady, hotspotsBridgeRef }) {
  // hotspots state is ONLY used to render the DOM nodes once.
  // Positions are updated every frame via direct DOM mutation from Hotspots.useFrame —
  // no setState / React re-render happens during camera movement.
  const [hotspots, setHotspots] = useState([])
  const [showHotspots, setShowHotspots] = useState(false)
  const [scene1LabelOpacity, setScene1LabelOpacity] = useState(1)
  const [currentPOI, setCurrentPOI] = useState(null)
  const fadeTimeoutRef = useRef(null)
  const hasShownRef = useRef(false)
  const isResettingRef = useRef(false)
  const [portraitMode, setPortraitMode] = useState(window.innerHeight > window.innerWidth)
  const [animationKey, setAnimationKey] = useState(0)
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const [hotspotsInteractive, setHotspotsInteractive] = useState(false)
  const interactiveTimeoutRef = useRef(null)

  // Stable map from hotspot name → DOM element, populated by callback refs on each dot div.
  // Hotspots.useFrame mutates positions directly through this map every frame.
  const dotRefsMap = useRef(new Map())

  // Expose dotRefsMap and the seedHotspots callback to the Hotspots (Canvas) component
  // via the shared hotspotsBridgeRef so useFrame can write directly to DOM elements.
  useEffect(() => {
    if (hotspotsBridgeRef) {
      hotspotsBridgeRef.current = {
        dotRefsMap: dotRefsMap.current,
        seedHotspots: setHotspots
      }
    }
    return () => {
      if (hotspotsBridgeRef) hotspotsBridgeRef.current = null
    }
  }, [hotspotsBridgeRef])
  
  // Generate random rotation for each POI label (sticker effect)
  const labelRotations = useMemo(() => {
    const rotations = {}
    const poiNames = ['POI_LP_Clamp', 'POI_LP_Fuel', 'POI_LP_Shack', 'POI_LP_Origin']
    poiNames.forEach(name => {
      // Random rotation between -3 and +3 degrees
      rotations[name] = (Math.random() * 6) - 3
    })
    return rotations
  }, [])
  
  // Listen for orientation changes
  useEffect(() => {
    const handleOrientationChange = () => {
      setPortraitMode(window.innerHeight > window.innerWidth)
    }
    
    window.addEventListener('orientationchange', handleOrientationChange)
    window.addEventListener('resize', handleOrientationChange)
    
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange)
      window.removeEventListener('resize', handleOrientationChange)
    }
  }, [])
  
  // Handle orientation changes when viewing a POI
  useEffect(() => {
    const handlePOIOrientationChange = () => {
      if (currentPOI) {
        setTimeout(() => {
          const isLandscape = window.innerWidth > window.innerHeight
          
          // Get the updated camera position for the current POI with new orientation
          const cameraConfig = getSaxavordCameraPosition(currentPOI, scenesRef, isMobilePortrait())
          
          if (cameraConfig) {
            window.dispatchEvent(new CustomEvent('scene-camera-move', {
              detail: {
                targetPosition: cameraConfig.position,
                targetLookAt: cameraConfig.target,
                duration: ANIMATION_TIMINGS.orientationChange
              }
            }))
          }
        }, 100)
      }
    }
    
    window.addEventListener('orientationchange', handlePOIOrientationChange)
    window.addEventListener('resize', handlePOIOrientationChange)
    
    return () => {
      window.removeEventListener('orientationchange', handlePOIOrientationChange)
      window.removeEventListener('resize', handlePOIOrientationChange)
    }
  }, [currentPOI, scenesRef])
  
  const isMobile = window.innerWidth <= 768
  const isMobilePortrait = () => window.innerWidth <= 767 && window.innerHeight > window.innerWidth

  // Positions are written directly by Hotspots.useFrame (priority 2) via dotRefsMap.
  // No rAF loop needed here — single-loop architecture eliminates the old stale-frame lag.
  
  const handleHotspotClick = useCallback((hotspot) => {
    // Check if it's a saxhq model POI (POI_HA1, POI_LP1, POI_SAT, POI_MET, POI_RAF, POI_HIS, etc.)
    // This includes POIs with or without camera positions
    if (hotspot.name.startsWith('POI_')) {
      // Google Analytics - track hotspot click
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'button_click',
        poi_label: getPOILabel(hotspot.name, true)
      })
      track('button_click', {
        poi_label: getPOILabel(hotspot.name, true)
      })

      // IMMEDIATELY hide labels when clicking any hotspot
      setShouldAnimate(false)
      setShowHotspots(false)
      setHotspotsInteractive(false)
      if (interactiveTimeoutRef.current) {
        clearTimeout(interactiveTimeoutRef.current)
        interactiveTimeoutRef.current = null
      }
      
      const cameraConfig = getSaxavordCameraPosition(hotspot.name, scenesRef, isMobilePortrait())
      
      // Always set the current POI and show text box
      setCurrentPOI(hotspot.name)
      onPOIStateChange(true, hotspot.name)
      
      // Only move camera if a camera position is defined for this POI
      if (cameraConfig) {
        window.dispatchEvent(new CustomEvent('scene-camera-move', {
          detail: {
            targetPosition: cameraConfig.position,
            targetLookAt: cameraConfig.target,
            duration: ANIMATION_TIMINGS.cameraMovement
          }
        }))
      }
    }
  }, [onPOIStateChange, scenesRef])
  
  const handleBackToDefault = useCallback(() => {
    // Check if current POI had a camera position
    const hadCameraPosition = currentPOI && getSaxavordCameraPosition(currentPOI, scenesRef, isMobilePortrait()) !== null
    
    // Update state immediately to trigger text box hide animation
    onPOIStateChange(false, null)
    setCurrentPOI(null)
    
    // Only move camera back to default if the POI had a camera position
    if (hadCameraPosition) {
      const isPortrait = window.innerWidth < window.innerHeight
      const isLandscape = !isPortrait
      const defaults = getDefaultCameraPosition('scene1', isLandscape)
      
      window.dispatchEvent(new CustomEvent('scene-camera-move', {
        detail: {
          targetPosition: defaults.position.clone(),
          targetLookAt: defaults.target.clone(),
          duration: ANIMATION_TIMINGS.cameraMovement
        }
      }))
    } else {
      // POI had no camera move (e.g. POI_MET) — show labels immediately.
      // Block the useEffect reset cycle with isResettingRef so it doesn't
      // clobber the state we're setting here, then clear it after one frame.
      isResettingRef.current = true
      clearTimeout(interactiveTimeoutRef.current)
      setShouldAnimate(true)
      setShowHotspots(true)
      hasShownRef.current = true
      requestAnimationFrame(() => {
        isResettingRef.current = false
        interactiveTimeoutRef.current = setTimeout(() => {
          setHotspotsInteractive(true)
        }, 400)
      })
    }
  }, [onPOIStateChange, currentPOI, scenesRef])
  
  // Pass handleBackToDefault to parent component
  useEffect(() => {
    if (onBackToDefaultReady) {
      onBackToDefaultReady(handleBackToDefault)
    }
  }, [onBackToDefaultReady, handleBackToDefault])
  
  // Reset animation when going back from POI
  useEffect(() => {
    // Only trigger the reset when coming back from POI
    if (!isAtPOI && hotspots.length > 0 && hasShownRef.current && !isResettingRef.current) {
      // Mark that we're in reset mode
      isResettingRef.current = true
      
      // Clear any existing timeout FIRST
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current)
        fadeTimeoutRef.current = null
      }
      
      // IMMEDIATELY disable animation
      setShouldAnimate(false)
      setShowHotspots(false)
      setHotspotsInteractive(false)
      hasShownRef.current = false
      setAnimationKey(prev => prev + 1)
      if (interactiveTimeoutRef.current) {
        clearTimeout(interactiveTimeoutRef.current)
        interactiveTimeoutRef.current = null
      }
      
      // Wait for elements to mount with opacity 0, then enable animation after 100ms
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          fadeTimeoutRef.current = setTimeout(() => {
            setShouldAnimate(true)
            hasShownRef.current = true
            setShowHotspots(true)
            isResettingRef.current = false
            // Enable interaction only after fade-in completes (2s delay + 1s transition)
            interactiveTimeoutRef.current = setTimeout(() => {
              setHotspotsInteractive(true)
            }, 3000)
          }, 100) // 100ms delay before CSS takes over
        })
      })
    }
  }, [isAtPOI, hotspots.length])
  
  useEffect(() => {
    if (hotspots.length === 0) {
      setShouldAnimate(false)
      setShowHotspots(false)
      setHotspotsInteractive(false)
      hasShownRef.current = false
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current)
        fadeTimeoutRef.current = null
      }
      if (interactiveTimeoutRef.current) {
        clearTimeout(interactiveTimeoutRef.current)
        interactiveTimeoutRef.current = null
      }
    } else if (!hasShownRef.current && !isAtPOI && !isResettingRef.current) {
      hasShownRef.current = true
      
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current)
        fadeTimeoutRef.current = null
      }
      
      // Wait 100ms then enable
      requestAnimationFrame(() => {
        fadeTimeoutRef.current = setTimeout(() => {
          if (!isResettingRef.current) {
            setShouldAnimate(true)
            setShowHotspots(true)
            // Enable interaction only after fade-in completes (2s delay + 1s transition)
            interactiveTimeoutRef.current = setTimeout(() => {
              setHotspotsInteractive(true)
            }, 3000)
          }
        }, 100) // 100ms delay before CSS takes over
      })
    }
  }, [hotspots, isAtPOI])

  return (
    <div className="hotspots-overlay">
      {hotspots.length > 0 && hotspots.map((hotspot) => {
        // Hide labels when at POI instead of unmounting them
        if (isAtPOI && !hotspot.name.startsWith('POI_NT')) return null
        const isNTIcon = hotspot.name.startsWith('POI_NT')

        // NT icons: still rendered conditionally (rare, not in rAF path).
        // Position is set by rAF loop via dotRefsMap; initial off-screen avoids flash.
        if (isNTIcon) {
          return hotspot.isVisible ? (
            <NTIcon
              key={hotspot.name}
              hotspot={hotspot}
              // Initial position off-screen; rAF loop moves it to the correct spot
              leftPosition="-9999px"
              topPosition="-9999px"
              scene1LabelOpacity={scene1LabelOpacity}
              isMobile={isMobile}
              handleHotspotClick={handleHotspotClick}
            />
          ) : null
        }

        if (hotspot.name.startsWith('BTN_')) return null

        const hasVisibleClass = (showHotspots && !isAtPOI && shouldAnimate)
        const isInteractive = hotspotsInteractive && hasVisibleClass
        // isNearRightEdge uses the initial hotspot.x from the seed render —
        // good enough since it only affects the CSS class, not the position
        const isNearRightEdge = hotspot.x > window.innerWidth - 250
        const transformValue = `rotate(${labelRotations[hotspot.name] || 0}deg)`

        return (
          <div
            key={`${hotspot.name}-${animationKey}`}
            // Callback ref: registers this DOM node in dotRefsMap so the rAF loop
            // can mutate its style.left / style.top directly every frame.
            ref={el => {
              if (el) dotRefsMap.current.set(hotspot.name, el)
              else dotRefsMap.current.delete(hotspot.name)
            }}
            className={`hotspot-label-item ${hasVisibleClass ? 'hotspot-visible' : ''} ${isInteractive ? 'hotspot-interactive' : ''} ${hotspot.name === 'POI_LP_Origin' ? 'black-arrow-origin' : ''} ${isNearRightEdge ? 'right-edge' : ''}`}
            style={{
              // Start off-screen; rAF loop writes the real position on the next frame
              left: '-9999px',
              top: '-9999px',
              transform: isNearRightEdge ? undefined : transformValue,
            }}
            onClick={() => handleHotspotClick(hotspot)}
          >
            {getPOILabel(hotspot.name, !portraitMode)}
          </div>
        )
      })}
    </div>
  )
}

// Title component
export function Title({ activePOI = null }) {
  // Determine title and subtitle based on active POI
  let mainTitle = 'SAXAVORD SPACEPORT'
  let subTitle = 'DARK STAR LABS PRESENTS'
  
  if (activePOI === 'POI_LP_Clamp') {
    mainTitle = 'PROPULSION'
    subTitle = 'LAUNCH PLATFORM'
  } else if (activePOI === 'POI_LP_Fuel') {
    mainTitle = 'POTENTIAL REVIVAL'
    subTitle = 'LAUNCH PLATFORM'
  } else if (activePOI === 'POI_LP_Shack') {
    mainTitle = 'LAUNCH HISTORY'
    subTitle = 'LAUNCH PLATFORM'
  } else if (activePOI === 'POI_LP_Origin') {
    mainTitle = 'BLACK ARROW'
    subTitle = 'THE ROCKET'
  }
  
  return (
    <div className="title-container">
      <div className="title-subtitle">{subTitle}</div>
      <div className="title-main">{mainTitle}</div>
      <div className="title-row">
        <div className="title-info-box">UK SPACE HISTORY</div>
      </div>
    </div>
  )
}

// TextBox component - displays content for POI sections
export function TextBox({ isAtPOI, activePOI, onBackToDefault, onClosingStateChange, onExpandedStateChange }) {
  const isMobile = window.innerWidth <= 767
  const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0)
  const isTablet = isTouchDevice && window.innerWidth > 767 && window.innerWidth <= 1366
  const isLandscape = window.innerWidth > window.innerHeight
  const isMobileLandscape = isMobile && isLandscape
  
  // Show text box for all POIs except in mobile landscape
  const shouldShow = isAtPOI && activePOI && !isMobileLandscape

  const [isVisible, setIsVisible] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const [contentPOI, setContentPOI] = useState(null)
  const [isExpanded, setIsExpanded] = useState(false)
  
  // Use ref to track if we were showing expanded content - prevents flash during close
  const wasExpandedRef = useRef(false)
  const isCollapsibleRef = useRef(false)
  const closingContentStateRef = useRef(null)
  
  const shouldShowExpandButton = (isMobile || isTablet) && isMounted
  const shouldShowBackButton = isMounted

  // Simplified: just show/hide based on shouldShow - no animations
  useEffect(() => {
    if (shouldShow) {
      setContentPOI(activePOI)
      setIsMounted(true)
      setIsVisible(true)
      // Only set isCollapsibleRef on initial mount, not on orientation changes
      if (!wasExpandedRef.current && isCollapsibleRef.current === false) {
        isCollapsibleRef.current = (isMobile || isTablet)
      }
      closingContentStateRef.current = null
      // Notify parent that we're not closing
      if (onClosingStateChange) onClosingStateChange(false)
    } else if (isMounted) {
      // Immediately hide and unmount - no animation delay
      wasExpandedRef.current = false
      setIsVisible(false)
      setIsExpanded(false)
      setIsMounted(false)
      setContentPOI(null)
      closingContentStateRef.current = null
      isCollapsibleRef.current = false
      // Notify parent immediately
      if (onClosingStateChange) onClosingStateChange(false)
    }
  }, [shouldShow, activePOI, isMobile, isTablet, isExpanded, isMounted, onClosingStateChange])
  
  // Track expanded state in ref and notify parent
  useEffect(() => {
    if (isExpanded) {
      wasExpandedRef.current = true
    }
    // Notify parent of expanded state change
    if (onExpandedStateChange) {
      onExpandedStateChange(isExpanded)
    }
  }, [isExpanded, onExpandedStateChange])

  if (!isMounted) return null

  const content = contentPOI ? getContentForPOI(contentPOI) : null
  if (!content) return null

  const isSaxavordPOI = contentPOI && (contentPOI === 'POI_HA1' || contentPOI === 'POI_LP1' || contentPOI === 'POI_SAT' || contentPOI === 'POI_MET' || contentPOI === 'POI_RAF' || contentPOI === 'POI_HIS')
  
  // Determine what content to show
  // During close animation, always show shortText (collapsed state)
  const showFullContent = isVisible && (!isCollapsibleRef.current || isExpanded)

  return (
    <div className={`text-box-container${isSaxavordPOI ? ' text-box-saxavord' : ''}${isVisible ? ' text-box-in' : ' text-box-out'}${isExpanded ? ' text-box-expanded' : ''}${shouldShowExpandButton ? ' text-box-collapsible' : ''}`}>
      <div className="text-box-content">
        <h2 className="text-box-title">{content.title}</h2>
        <div className="text-box-body">
          {showFullContent ? (
            <>
              <p style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: content.body }} />
              {content.bullets && content.bullets.length > 0 && (
                <div className="bullet-timeline">
                  {content.bullets.map((bullet, index) => (
                    <div key={index} className="bullet-timeline-event">
                      <div className="bullet-timeline-line"></div>
                      <div className="bullet-timeline-desc">
                        <strong className="bullet-timeline-label">{bullet.label}</strong>
                        <div className="bullet-timeline-text">
                          {Array.isArray(bullet.text) ? (
                            bullet.text.map((paragraph, pIndex) => (
                              <p key={pIndex} className="bullet-timeline-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                            ))
                          ) : (
                            <p className="bullet-timeline-paragraph" dangerouslySetInnerHTML={{ __html: bullet.text }} />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="text-box-short-body" style={{ whiteSpace: 'pre-line' }}>{content.shortBody || content.body}</p>
          )}
        </div>
      </div>
      <div className="text-box-buttons">
        {shouldShowBackButton && (
          <button 
            className="text-box-back-button"
            onClick={() => {
              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({ event: 'button_click', poi_label: `${getPOILabel(contentPOI)} - Back` })
              track('button_click', { poi_label: `${getPOILabel(contentPOI)} - Back` })
              onBackToDefault()
            }}
          >
            Back
          </button>
        )}
        {shouldShowExpandButton && (
          <button 
            className="text-box-expand-button"
            onClick={() => {
              const expanding = !isExpanded
              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({ event: 'button_click', poi_label: `${getPOILabel(contentPOI)} - ${expanding ? 'More' : 'Close'}` })
              track('button_click', { poi_label: `${getPOILabel(contentPOI)} - ${expanding ? 'More' : 'Close'}` })
              setIsExpanded(expanding)
            }}
          >
            {isExpanded ? 'Close' : 'More'}
          </button>
        )}
      </div>
    </div>
  )
}

// Logo Group - Bottom left on desktop and tablet
export function LogoGroup({ isAtPOI, isTextBoxClosing, isTextBoxExpanded }) {
  const isDesktop = window.innerWidth > 1366
  const isTablet = window.innerWidth >= 768 && window.innerWidth <= 1366
  const isMobilePortrait = window.innerWidth <= 767 && window.innerHeight > window.innerWidth
  const isLandscape = window.innerWidth > window.innerHeight
  const isTabletLandscape = isTablet && isLandscape
  
  if (!isDesktop && !isTablet && !isMobilePortrait) return null
  
  // TABLET LANDSCAPE: Only hide when text box is expanded (maximized)
  // TABLET PORTRAIT & MOBILE PORTRAIT: Hide when at POI (text box is open)
  if (isTabletLandscape) {
    // Tablet landscape - only hide if text box is expanded
    if (isTextBoxExpanded) return null
  } else if (isTablet || isMobilePortrait) {
    // Tablet portrait or mobile portrait - hide when at POI
    if (isAtPOI || isTextBoxClosing) return null
  }
  
  const opacity = 1
  const isSpaceCommMode = new URLSearchParams(window.location.search).get('sc') === 'true'
  
  return (
    <div className="logo-group" style={{ opacity, transition: 'opacity 0.6s ease' }}>
      <div className="logo-dsl-wrapper">
        <span className="logo-dsl-developed-by">developed by</span>
        <img 
          src="/textures/logos/DSL Logo Stacked Light and Orange.svg" 
          alt="Dark Star Labs" 
          className="logo-image logo-dsl" 
          onClick={isSpaceCommMode ? undefined : () => {
            window.dataLayer = window.dataLayer || []
            window.dataLayer.push({ event: 'button_click', poi_label: 'Dark Star Labs Logo' })
            track('button_click', { poi_label: 'Dark Star Labs Logo' })
            window.open('https://darkstarlabs.io', '_blank')
          }}
          style={{ cursor: isSpaceCommMode ? 'default' : 'pointer', pointerEvents: 'auto' }}
        />
      </div>
    </div>
  )
}

// RAAF Woomera Badge - Bottom right on desktop only
export function WoomeraBadge({ isAtPOI }) {
  const isDesktop = window.innerWidth > 1366
  
  if (!isDesktop) return null
  
  const dotOffsetX = 18
  const dotOffsetY = 20
  const opacity = isAtPOI ? 0 : 1
  
  return (
    <div className="woomera-badge" style={{ opacity, transition: 'opacity 0.6s ease' }}>
      <img src="/textures/logos/RAAF-Woomera.webp" alt="RAAF Woomera" className="woomera-image" />
      <div 
        className="woomera-dot" 
        style={{ 
          transform: `translate(${dotOffsetX}px, ${dotOffsetY}px)` 
        }}
      >
        <div className="woomera-dot-label">RAAF-WOOMERA</div>
      </div>
    </div>
  )
}

// Envelope Button - Top right
export function EnvelopeButton({ isAtPOI, bannerDismissed, newsletterVisible }) {
  if (isAtPOI || newsletterVisible) return null
  return (
    <div className={`envelope-button-wrapper${bannerDismissed ? ' banner-dismissed' : ''}`} onClick={() => {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({ event: 'button_click', poi_label: 'Newsletter - Open' })
      track('button_click', { poi_label: 'Newsletter - Open' })
      window.open('http://eepurl.com/ifM-Vj', '_blank')
    }} style={{ cursor: 'pointer' }}>
      <span className="envelope-button-label">SIGN UP TO THE NEWSLETTER</span>
      <button className="envelope-button" aria-label="Sign up to the newsletter">
        <img src="/svg/envelope-open-text.svg" alt="" width={32} height={32} />
      </button>
    </div>
  )
}

// SaxaVord Logo - Top left
export function SaxaVordLogo() {
  const opacity = 1
  
  return (
    <div className="saxavord-logo-container" style={{ opacity, transition: 'opacity 0.6s ease' }}>
      <img 
        src="/textures/logos/SaxaVordLogo_bluText.png" 
        alt="SaxaVord Spaceport" 
        className="saxavord-logo" 
        onClick={() => {
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({ event: 'button_click', poi_label: 'SaxaVord Logo' })
          track('button_click', { poi_label: 'SaxaVord Logo' })
          window.open('https://saxavord.com/', '_blank')
        }}
        style={{ cursor: 'pointer', pointerEvents: 'auto' }}
      />
    </div>
  )
}

// Mute Button - toggles ambient audio
export function MuteButton({ muted, onToggle }) {
  return (
    <button
      className="mute-button"
      onClick={() => {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ event: 'button_click', poi_label: muted ? 'Audio - Unmute' : 'Audio - Mute' })
        track('button_click', { poi_label: muted ? 'Audio - Unmute' : 'Audio - Mute' })
        onToggle()
      }}
      aria-label={muted ? 'Unmute audio' : 'Mute audio'}
    >
      {muted ? (
        // Volume off
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
          <line x1="23" y1="9" x2="17" y2="15"/>
          <line x1="17" y1="9" x2="23" y2="15"/>
        </svg>
      ) : (
        // Volume on
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
        </svg>
      )}
    </button>
  )
}
